export type FindElementOptions = {
  selector?: string
  text?: string
  textElementTypes?: string[]
  targetElementTypes?: string[]
  scopeSelector?: string
}

const MAX_FALLBACK_DEPTH = 10

function findElementByXPath(
  xpath: string,
  contextNode: HTMLElement | Document
): HTMLElement | null {
  const result = document.evaluate(
    xpath,
    contextNode,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  )
  // eslint-disable-next-line
  return result.singleNodeValue as HTMLElement | null
}

function findElementByText(
  text: string,
  textElementTypes: string[],
  targetElementTypes?: string[],
  scopeSelector?: string
): HTMLElement {
  const contextNode = scopeSelector ? document.querySelector<HTMLElement>(scopeSelector) : document

  if (!contextNode) {
    throw new Error(`Scope with selector "${scopeSelector}" not found`)
  }

  for (const textType of textElementTypes) {
    // Find an element of type textType that contains the text
    const textElementXpath = `//${textType}[contains(., '${text}')]`
    const textElement = findElementByXPath(textElementXpath, contextNode)

    if (textElement) {
      if (targetElementTypes && targetElementTypes.length > 0) {
        for (const targetType of targetElementTypes) {
          // Find the closest element to the textElement of type targetType
          const targetElementXpath = `ancestor-or-self::*[count(.//${targetType}) = 1]//${targetType}[1]`
          const targetElement = findElementByXPath(targetElementXpath, textElement)
          if (targetElement) {
            return targetElement
          }
        }
      } else {
        return textElement
      }
    }
  }

  const anchor = findElementByXPath(
    `//*[contains(., '${text}') and not(.//*[contains(., '${text}')])]`,
    contextNode
  )

  if (anchor) {
    const types = targetElementTypes?.length ? targetElementTypes : textElementTypes
    let node: HTMLElement | null = anchor
    let depth = 0

    while (node) {
      for (const targetType of types) {
        const found = findElementByXPath(`.//${targetType}[normalize-space(.)!='']`, node)
        if (found) {
          return found
        }
      }
      if (node === contextNode || depth >= MAX_FALLBACK_DEPTH) break
      node = node.parentElement
      depth += 1
    }
  }

  throw new Error(`Element with text "${text}" not found`)
}

export async function findElement({
  selector,
  text,
  textElementTypes,
  targetElementTypes,
  scopeSelector,
}: FindElementOptions): Promise<HTMLElement> {
  if (selector) {
    const element = document.querySelector<HTMLElement>(selector)
    if (element) {
      return element
    }
    throw new Error(`Element with selector "${selector}" not found`)
  }

  if (text && textElementTypes && textElementTypes.length > 0) {
    return findElementByText(text, textElementTypes, targetElementTypes, scopeSelector)
  }

  throw new Error('Either "selector" or "text" and "textElementTypes" must be provided')
}
